import { useState, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
} from "@lendcord/concord";
import { useLazyGetDocumentDownloadUrlQuery } from "@/features/profile/api/profile.api";
import { formatTimestampDate } from "@/app/dateFormat";
import type { FixedDocumentType, ProfileDocument } from "@/app/profile/profile.types";

// ADDITIONAL_DOCUMENT_N (agent-onboarding-only) has no dedicated copy — it
// falls back to the generic "Other" label below.
const DOCUMENT_TYPE_LABEL_IDS: Record<FixedDocumentType, string> = {
  AADHAAR_FRONT: "lender.profile.documentTypeAadhaarFront",
  AADHAAR_BACK: "lender.profile.documentTypeAadhaarBack",
  PAN: "lender.profile.documentTypePan",
  BANK_ACCOUNT: "lender.profile.documentTypeBankAccount",
  PHOTO: "lender.profile.documentTypePhoto",
  ADDRESS_PROOF: "lender.profile.documentTypeAddressProof",
  ID_PROOF: "lender.profile.documentTypeIdProof",
  OTHER: "lender.profile.documentTypeOther",
};

function documentTypeLabelId(type: ProfileDocument["type"]): string {
  return DOCUMENT_TYPE_LABEL_IDS[type as FixedDocumentType] ?? "lender.profile.documentTypeOther";
}

const DOCUMENT_STATUS_BADGE_STATUS: Record<ProfileDocument["status"], "success" | "warning" | "error"> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const DOCUMENT_STATUS_LABEL_IDS: Record<ProfileDocument["status"], string> = {
  PENDING: "lender.profile.documentStatusPending",
  APPROVED: "lender.profile.documentStatusApproved",
  REJECTED: "lender.profile.documentStatusRejected",
};

function formatUploadedAt(value: string): string {
  const parsed = new Date(value);
  const time = parsed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase();
  return `${formatTimestampDate(value)} ${time}`;
}

function FileIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M12 17v-6M9.5 13.5 12 11l2.5 2.5" />
    </svg>
  );
}

function MoreVerticalIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

interface DocumentCardProps {
  document: ProfileDocument;
  onUploadAgain: () => void;
}

export function DocumentCard({ document, onUploadAgain }: DocumentCardProps) {
  const intl = useIntl();
  const [isDownloading, setIsDownloading] = useState(false);
  const [fetchDownloadUrl] = useLazyGetDocumentDownloadUrlQuery();

  async function handleDownload() {
    setIsDownloading(true);
    try {
      const { url } = await fetchDownloadUrl(document.id).unwrap();
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-surface-raised p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-surface text-text-muted">
        <FileIcon />
      </div>
      <div className="min-w-0 flex-1">
        <Text as="p" size="sm" className="truncate font-medium">
          {intl.formatMessage({ id: documentTypeLabelId(document.type) })}
        </Text>
        <Text as="p" size="xs" variant="muted">
          <FormattedMessage
            id="lender.profile.uploadedOnLabel"
            values={{ timestamp: formatUploadedAt(document.uploadedAt) }}
          />
        </Text>
      </div>
      <Badge status={DOCUMENT_STATUS_BADGE_STATUS[document.status]}>
        {intl.formatMessage({ id: DOCUMENT_STATUS_LABEL_IDS[document.status] })}
      </Badge>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "lender.profile.documentMenuLabel" })}
            className="shrink-0 text-text-muted hover:text-text"
          >
            <MoreVerticalIcon className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={handleDownload} disabled={isDownloading}>
            <FormattedMessage id="lender.profile.downloadDocumentButton" />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onUploadAgain}>
            <FormattedMessage id="lender.profile.uploadAgainButton" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
